import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';

// ============================================================================
// COLUMN SELECTOR — shared "Manage Columns" toolbar control (template standard)
// Rendered inside ModuleHeader via customToolbarItems. Parent owns the visible
// list (persist to localStorage) and passes onApply / onReset.
// ============================================================================

export interface ColumnDefinition {
  id: string;
  label: string;
  /** Kept for callers that map definitions onto grid columns; not used here (template parity). */
  field?: string;
  /** Cannot be hidden (e.g. selection checkbox, actions). */
  required?: boolean;
  defaultVisible?: boolean;
  /** Grid hints carried alongside the definition (template parity; the shared tax-master module sets them). */
  width?: number;
  sortable?: boolean;
}

export interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
}

/** Owns visible-column state + localStorage persistence for a screen's column selector. */
export function useColumnVisibility(storageKey: string, columns: ColumnDefinition[]) {
  const defaults = useMemo(() => columns.filter(c => c.defaultVisible !== false).map(c => c.id), [columns]);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    try { const s = localStorage.getItem(storageKey); if (s) return JSON.parse(s); } catch { /* ignore */ }
    return defaults;
  });
  const onApply = useCallback((cols: string[]) => { setVisibleColumns(cols); try { localStorage.setItem(storageKey, JSON.stringify(cols)); } catch { /* ignore */ } }, [storageKey]);
  const onReset = useCallback(() => { setVisibleColumns(defaults); try { localStorage.removeItem(storageKey); } catch { /* ignore */ } }, [storageKey, defaults]);
  return { visibleColumns, onApply, onReset };
}

export const ColumnSelector: React.FC<ColumnSelectorProps> = ({ columns, visibleColumns: initialVisible, onApply, onReset }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setSelectedColumns(initialVisible); }, [initialVisible]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) { setIsOpen(false); setSelectedColumns(initialVisible); }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column?.required) return;
    setSelectedColumns(prev => prev.includes(columnId) ? prev.filter(id => id !== columnId) : [...prev, columnId]);
  };

  const handleSelectAll = () => {
    const allIds = columns.filter(c => !c.required).map(c => c.id);
    const requiredIds = columns.filter(c => c.required).map(c => c.id);
    if (selectedColumns.length === columns.length) setSelectedColumns(requiredIds);
    else setSelectedColumns([...requiredIds, ...allIds]);
  };

  const handleApply = () => { onApply(selectedColumns); setIsOpen(false); };
  const handleCancel = () => { setSelectedColumns(initialVisible); setIsOpen(false); };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={panelRef}>
      <button className={`btn-icon ${isOpen ? 'active' : ''}`} onClick={() => setIsOpen(!isOpen)} title="Manage Columns"
        style={isOpen ? { background: 'var(--accent-gradient)', color: 'white', borderColor: 'var(--primary)' } : {}}>
        <IconSystem.grid size={18} />
      </button>
      {isOpen && (
        <div style={{ position: 'absolute', top: 'calc(100% + 0.5rem)', right: 0, width: '280px', maxHeight: '450px', background: 'var(--bg-primary)', border: '2px solid var(--primary)', borderRadius: '0.75rem', boxShadow: '0 12px 32px rgba(0,0,0,0.15)', zIndex: 1000, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '2px solid var(--border-color)', background: 'var(--bg-secondary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Manage Columns</h3>
            <button onClick={() => setIsOpen(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><IconSystem.x size={16} /></button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem 0', minHeight: 0 }}>
            {columns.map(column => (
              <div key={column.id} onClick={() => toggleColumn(column.id)}
                style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 1rem', cursor: column.required ? 'not-allowed' : 'pointer', gap: '0.625rem', opacity: column.required ? 0.6 : 1 }}>
                <div style={{ width: '1rem', height: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid var(--border-color)', borderRadius: '0.25rem', flexShrink: 0, ...(selectedColumns.includes(column.id) ? { background: 'var(--accent-gradient)', borderColor: 'var(--primary)', color: 'white' } : {}) }}>
                  {selectedColumns.includes(column.id) && <IconSystem.check size={12} />}
                </div>
                <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-primary)', userSelect: 'none' }}>{column.label || '(Select)'}{column.required && ' *'}</span>
              </div>
            ))}
          </div>
          <div style={{ padding: '0.75rem', borderTop: '2px solid var(--border-color)', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <button onClick={handleSelectAll} style={{ flex: 1, padding: '0.375rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.375rem', cursor: 'pointer' }}>{selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}</button>
              <button onClick={() => setConfirmResetOpen(true)} style={{ flex: 1, padding: '0.375rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.375rem', cursor: 'pointer' }}>Reset</button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={handleCancel} className="btn btn-secondary">Cancel</button>
              <button onClick={handleApply} className="btn btn-primary">Apply</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog open={confirmResetOpen} title="Reset Columns" message="Reset to default column configuration?"
        confirmLabel="Reset" cancelLabel="Cancel" onConfirm={() => { onReset(); setConfirmResetOpen(false); setIsOpen(false); }} onCancel={() => setConfirmResetOpen(false)} />
    </div>
  );
};

export default ColumnSelector;
